// src/components/jobseeker/JobSearch.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { searchJobs, getJobById, addJobApplication, hasJobSeekerApplied } from '../../services/firestore';
import { useAuth } from '../../contexts/AuthContext';

const JobSearch = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    status: 'active'
  });
  const { currentUser, userProfile } = useAuth();
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applySuccess, setApplySuccess] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  
  // OPTIMIZATION: Using useCallback to prevent unnecessary re-creation of functions
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { jobs: jobResults } = await searchJobs(searchFilters);
      setJobs(jobResults);
    } catch (err) {
      console.error('Job search error:', err);
      setError('Error searching for jobs: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);
  
  // Search for jobs when component loads or filters change
  useEffect(() => {
    handleSearch();
  }, [handleSearch]);
  
  // Check if user has applied to jobs
  useEffect(() => {
    const checkAppliedJobs = async () => {
      if (!currentUser) return;
      
      const applied = new Set();
      for (const job of jobs) {
        const hasApplied = await hasJobSeekerApplied(job.id, currentUser.uid);
        if (hasApplied) {
          applied.add(job.id);
        }
      }
      setAppliedJobs(applied);
    };
    
    checkAppliedJobs();
  }, [currentUser, jobs]);
  
  const handleJobClick = useCallback(async (jobId) => {
    try {
      setLoading(true);
      const jobDetails = await getJobById(jobId);
      setSelectedJob(jobDetails);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Error loading job details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const handleCloseDetails = useCallback(() => {
    setSelectedJob(null);
  }, []);
  
  // OPTIMIZATION: Using useMemo for derived values to prevent recalculation on every render
  
  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }, []);
  
  // Get days of week as string
  const getScheduleString = useCallback((schedule) => {
    if (!schedule || Object.keys(schedule).length === 0) return 'No schedule set';
    
    const days = Object.keys(schedule).map(day => {
      const { startTime, endTime } = schedule[day];
      if (startTime && endTime) {
        return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${startTime} - ${endTime}`;
      }
      return null;
    }).filter(Boolean);
    
    return days.join(', ');
  }, []);
  
  // Helper to format date and time separately
  const getDateAndTime = (timestamp) => {
    if (!timestamp) return { date: '', time: '' };
    const dateObj = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return {
      date: dateObj.toLocaleDateString(),
      time: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };
  
  const handleApply = async () => {
    if (!currentUser || !selectedJob) return;
    setApplyLoading(true);
    setApplyError(null);
    setApplySuccess(null);
    try {
      await addJobApplication({
        jobId: selectedJob.id,
        providerUid: selectedJob.providerUid || null,
        parentUid: selectedJob.parentUid || null,
        jobSeekerUid: currentUser.uid,
        jobSeekerName: userProfile?.displayName || '',
        jobTitle: selectedJob.title,
        status: 'pending',
      });
      setApplySuccess('Application submitted!');
    } catch (err) {
      setApplyError('Failed to apply: ' + err.message);
    } finally {
      setApplyLoading(false);
    }
  };
  
  // Helper to format job description with line breaks
  function formatDescription(text) {
    if (!text) return null;
    return text.split(/\n+/).map((line, idx) =>
      line.trim() === '' ? <br key={idx}/> : <p key={idx}>{line}</p>
    );
  }
  
  // OPTIMIZATION: Extracting modal to a separate component
  const JobDetailsModal = useMemo(() => {
    if (!selectedJob) return null;
    
    return (
      <div className="job-details-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>{selectedJob.title}</h2>
            <button 
              className="close-button"
              onClick={handleCloseDetails}
              aria-label="Close details"
            >
              &times;
            </button>
          </div>
          
          <div className="modal-body">
            <div className="job-header">
              <h3>{selectedJob.title}</h3>
              <span className={`job-type ${selectedJob.duration}`}>
                {selectedJob.duration?.charAt(0).toUpperCase() + selectedJob.duration?.slice(1) || 'Job'}
              </span>
            </div>
            
            <div className="job-details">
              <div className="job-description">{formatDescription(selectedJob.description)}</div>
              
              <div className="job-info">
                {selectedJob.startDate && (
                  <p>
                    <strong>Period: </strong>
                    {formatDate(selectedJob.startDate)}
                    {selectedJob.endDate && ` to ${formatDate(selectedJob.endDate)}`}
                  </p>
                )}
                
                {selectedJob.schedule && Object.keys(selectedJob.schedule).length > 0 && (
                  <p>
                    <strong>Schedule: </strong>
                    {getScheduleString(selectedJob.schedule)}
                  </p>
                )}
                
                {selectedJob.hourlyRate && (
                  <p>
                    <strong>Rate: </strong>
                    ${selectedJob.hourlyRate}/hour
                  </p>
                )}
              </div>
              
              {(selectedJob.qualifications?.length > 0 || selectedJob.responsibilities?.length > 0) && (
                <div className="job-lists">
                  {selectedJob.qualifications?.length > 0 && (
                    <div className="job-qualifications">
                      <h4>Qualifications</h4>
                      <ul>
                        {selectedJob.qualifications.map((qualification, index) => (
                          <li key={index}>{qualification}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedJob.responsibilities?.length > 0 && (
                    <div className="job-responsibilities">
                      <h4>Responsibilities</h4>
                      <ul>
                        {selectedJob.responsibilities.map((responsibility, index) => (
                          <li key={index}>{responsibility}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {selectedJob.requirements && (
                <div className="job-requirements">
                  <h4>Additional Requirements</h4>
                  <p>{selectedJob.requirements}</p>
                </div>
              )}
            </div>
            
            <div className="apply-section">
              {applyError && <div className="error-message">{applyError}</div>}
              {applySuccess && <div className="success-message">{applySuccess}</div>}
              <button className="apply-button" onClick={handleApply} disabled={applyLoading || applySuccess}>
                {applyLoading ? 'Applying...' : applySuccess ? 'Applied' : 'Apply for this Job'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [selectedJob, formatDate, getScheduleString, handleCloseDetails, handleApply, applyLoading, applyError, applySuccess]);
  
  // OPTIMIZATION: Extracting JobCard to a separate component
  const JobCard = useCallback(({ job, onClick }) => {
    const { date, time } = getDateAndTime(job.createdAt);
    return (
    <div className="job-card">
      <h3>{job.title}</h3>
      <div className="job-short-info">
        <p>{job.duration || 'Temporary'}</p>
        <p>{job.description?.substring(0, 50) || 'No description'}{job.description?.length > 50 ? '...' : ''}</p>
          <p><strong>Posted Date:</strong> {date}</p>
          <p><strong>Posted Time:</strong> {time}</p>
        {job.hourlyRate && (
          <p><strong>Rate:</strong> ${job.hourlyRate}/hour</p>
        )}
        {job.startDate && job.endDate && (
          <p><strong>Period:</strong> {formatDate(job.startDate)} to {formatDate(job.endDate)}</p>
        )}
      </div>
        <div className="job-actions">
      <button 
        className="view-details-button"
        onClick={onClick}
      >
        View Details
      </button>
          {currentUser && (
            <button 
              className={`apply-button ${appliedJobs.has(job.id) ? 'applied' : ''}`}
              onClick={() => handleJobClick(job.id)}
              disabled={appliedJobs.has(job.id)}
            >
              {appliedJobs.has(job.id) ? 'Already Applied' : 'Apply Now'}
            </button>
          )}
        </div>
    </div>
    );
  }, [formatDate, currentUser, appliedJobs]);
  
  return (
    <div className="job-search">
      <h1>Find Jobs</h1>
      
      <div className="search-controls">
        <button 
          className="refresh-button"
          onClick={handleSearch} 
          disabled={loading}
          aria-label="Refresh job listings"
        >
          {loading ? 'Searching...' : 'Refresh Jobs'}
        </button>
      </div>
      
      {error && <div className="error-message" role="alert">{error}</div>}
      
      {/* Job Results */}
      <div className="job-results">
        <h2>Available Jobs</h2>
        
        {loading && !selectedJob && <p aria-live="polite">Loading jobs...</p>}
        
        {!loading && jobs.length === 0 && (
          <p className="no-jobs" aria-live="polite">No jobs found. Please try again later.</p>
        )}
        
        {jobs.map(job => (
          <JobCard 
            key={job.id} 
            job={job} 
            onClick={() => handleJobClick(job.id)} 
          />
        ))}
      </div>
      
      {/* Job Details Modal */}
      {JobDetailsModal}
    </div>
  );
};

export default React.memo(JobSearch);